/* We Are Jolies — B2B live shipping rate (D88.4 / D120)
 *
 * Refines the server-rendered 50d shipping row with the REAL rate from
 * `/cart/shipping_rates.json`, on both recaps (cart page + drawer).
 *
 * 🔴 THE LAYER MAY ONLY EVER REFINE. 50d's server-rendered table is the first paint and the
 * fallback, and every failure path here is "do nothing" — no quote, no destination, an empty
 * response, an abort, a network error, a 422: the server's state stands untouched. That is not
 * defensiveness, it is rule 21a: the table is the honest floor and a live layer that blanked it on
 * failure would be strictly worse than no layer.
 *
 * 🔴 WHY ASYNC AT ALL — the endpoint costs ~900 ms and is SERIALISED per session (measured
 * 2026-08-16, D120.3): twelve parallel requests all returned 200, no 429 and no Retry-After, but
 * they completed ~900 ms apart, one at a time. So:
 *   · there is no synchronous first paint to be had — hence 50d stays;
 *   · parallelism buys NOTHING, and a burst just builds a queue;
 *   · the queue resolves IN ORDER, so without an abort the LAST-ARRIVING answer can be an OLDER
 *     query's. That is a correctness bug wearing a performance costume, and it is why every
 *     supersede aborts the request in flight rather than merely ignoring its result.
 *
 * 🔴 WHAT THE QUOTE DECIDES, AND WHAT IT STILL CANNOT (D120.1). The quote answers « what does this
 * buyer pay right now », INCLUDING whether the franco applies — measured: 294 € returns 10.00 and
 * 301 € returns 0.00, evaluated against the B2B price-list total. So the francoed STATE comes from
 * here and must not also be computed from the setting; two sources of truth for one value is this
 * engagement's recurring defect (D119.2). What the quote never reveals is the THRESHOLD NUMBER —
 * it says 0.00, never « 300 » — so the gauge's milestone stays setting-driven. Do not try to
 * reverse-engineer the threshold by bracketing quotes; that is the serialised endpoint's worst case.
 *
 * 🔴 EXACT vs FROM IS NOW DERIVED FROM THE RESPONSE, not from the zone table: one eligible rate is
 * exact, several make the cheapest a FLOOR (operator ruling D88.4 §2 — cheapest eligible, prefixed
 * « À partir de », and deliberately NOT folded into the total, because adding a floor would print a
 * total that is certainly too low). Measured against the table's own verdicts: France returns one
 * rate, Guadeloupe returns two (Colissimo 22 + DHL 64), USA returns one. The live derivation agrees
 * with the table everywhere it was checked AND self-corrects when the merchant edits a profile.
 */
(function () {
  'use strict';

  var CTX = '[data-b2b-ship-ctx]';
  var ARMED = 'data-b2b-ship-armed';

  function money(cents, format) {
    // order-pad.js owns the formatter; it is exposed rather than copied so a money format change
    // cannot land in one file and not the other.
    if (window.WAJ && typeof window.WAJ.formatMoney === 'function') {
      return window.WAJ.formatMoney(cents, format);
    }
    // Reached only if order-pad.js failed to load, in which case the cart has larger problems.
    return (cents / 100).toFixed(2).replace('.', ',') + ' €';
  }

  var inflight = null;
  var scheduled = false;

  function fetchRate(ctx, signal) {
    var p = new URLSearchParams();
    p.set('shipping_address[zip]', ctx.zip);
    p.set('shipping_address[country]', ctx.country);
    // Always sent: mandatory for province-countries (US/CA are a 422 without it), ignored elsewhere.
    p.set('shipping_address[province]', ctx.province || '');

    var root = (window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/');
    return fetch(root + 'cart/shipping_rates.json?' + p.toString(), {
      signal: signal,
      headers: { Accept: 'application/json' }
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (body) {
        if (!body || !body.shipping_rates || !body.shipping_rates.length) return null;
        var cents = body.shipping_rates
          .map(function (r) { return Math.round(parseFloat(r.price) * 100); })
          .filter(function (c) { return isFinite(c); });
        if (!cents.length) return null;
        cents.sort(function (a, b) { return a - b; });
        // count drives exact-vs-from; cheapest is the operator's ruled number either way.
        return { cents: cents[0], count: cents.length };
      });
  }

  function apply(recap, ctx, rate) {
    var row = recap.querySelector('[data-b2b-ship-row]');
    var value = recap.querySelector('[data-b2b-ship-value]');
    var totalValue = recap.querySelector('[data-b2b-total-value]');
    var totalLabel = recap.querySelector('[data-b2b-total-label]');
    if (!row || !value || !totalValue) return;

    var free = rate.cents === 0;
    var isFrom = !free && rate.count > 1;

    // The row may have been rendered latent (hidden) because the zone table could not price this
    // destination. A live quote is exactly the case that earns it a row.
    row.hidden = false;

    if (free) {
      // Textual, not markup: the « ✓ » is a span the server rendered and we keep whatever it is.
      value.textContent = ctx.freeLabel;
      var ok = document.createElement('span');
      ok.className = 'b2bg__ok';
      ok.textContent = '✓';
      value.appendChild(document.createTextNode(' '));
      value.appendChild(ok);
    } else {
      var text = money(rate.cents, ctx.moneyFormat);
      if (isFrom && ctx.fromPrefix) text = ctx.fromPrefix + ' ' + text;
      value.textContent = text;
    }

    // A « from » price is a FLOOR and stays OUT of the total — the total then carries the
    // « hors livraison » qualifier instead. Identical rule to the server's, so the two can never
    // print different arithmetic for one cart.
    var folded = (!free && !isFrom) ? rate.cents : 0;
    totalValue.textContent = money(ctx.cartTotal + folded, ctx.moneyFormat);

    if (totalLabel) {
      var note = totalLabel.querySelector('[data-b2b-total-note]');
      if (isFrom && ctx.exclNote) {
        if (!note) {
          note = document.createElement('span');
          note.setAttribute('data-b2b-total-note', '');
          note.className = totalLabel.getAttribute('data-b2b-note-class') || '';
          totalLabel.appendChild(note);
        }
        note.textContent = ctx.exclNote;
      } else if (note) {
        note.remove();
      }
    }
  }

  function process(node) {
    node.setAttribute(ARMED, '');

    var ctx;
    try { ctx = JSON.parse(node.textContent); } catch (e) { return; }
    if (!ctx || !ctx.zip || !ctx.country) return;

    var recap = node.closest('[data-b2b-recap]');
    if (!recap) return;

    // Supersede: abort the request in flight rather than letting the serialised queue resolve an
    // older query's answer last. See the header note — this is correctness, not tidiness.
    if (inflight) inflight.abort();
    var controller = new AbortController();
    inflight = controller;

    fetchRate(ctx, controller.signal)
      .then(function (rate) {
        if (controller.signal.aborted || !rate) return;
        apply(recap, ctx, rate);
      })
      .catch(function () { /* 422 · abort · offline — the server's 50d row stands. */ })
      .then(function () { if (inflight === controller) inflight = null; });
  }

  function scan() {
    scheduled = false;
    var nodes = document.querySelectorAll(CTX + ':not([' + ARMED + '])');
    for (var i = 0; i < nodes.length; i++) process(nodes[i]);
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(scan);
  }

  // 🔴 WHY AN OBSERVER AND NOT JUST THE CART EVENTS. `cart:updated` / `cart:refresh` announce the
  // MUTATION; Focal re-renders the sections ASYNCHRONOUSLY afterwards, so at event time the recap
  // in the DOM is still the old one. The theme already learned this the expensive way — a fixed
  // delay « can only ever be lucky » (order-pad.js, the drawer-scroll fix). So we wait for the
  // actual render: a fresh context node appearing IS the signal, and it is the thing we need
  // rather than a proxy for it. The events are still listened to because they cost nothing and
  // cover the case where the node is re-rendered identically.
  if (window.MutationObserver) {
    new MutationObserver(schedule).observe(document.documentElement, { childList: true, subtree: true });
  }
  document.addEventListener('cart:updated', schedule);
  document.addEventListener('cart:refresh', schedule);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule);
  } else {
    schedule();
  }
})();
